import { useState } from 'react';
import { logger } from '@fiverr-private/obs';
import { raiseToast } from '@fiverr-private/toast';
import pathfinder from '@fiverr-private/pathfinder';
import { changeBriefStatus, Gql } from '@fiverr-private/dynamic_matching';
import { CloseAction, DmBriefCloseReason } from '../../types';
import { appTranslate } from '../../i18n';
import { briefStatusChangeClickAction } from '../../monitoring';
import { getCloseReasonForAction } from '../../utils';
import { translationKeyByCloseAction } from './constants';

export const redirectToBriefsPageAfterDelete = () => {
    window.location.href = pathfinder(
        'briefs_lobby_page',
        {},
        {
            query: {
                source: 'delete_brief',
            },
        }
    );
};

export interface UseCloseBriefModalProps {
    closeAction: CloseAction;
    onSuccess: (closeAction: CloseAction) => void;
    onError: (closeAction: CloseAction) => void;
    briefId: string;
    source?: string;
}

export const useCloseBriefModal = ({ briefId, closeAction, onSuccess, onError, source }: UseCloseBriefModalProps) => {
    const [isModalOpen, setIsModalOpen] = useState(false);

    const reportProps = {
        briefId,
        statusChosen: closeAction,
    };

    const onConfirmHandler = async (closeReason?: DmBriefCloseReason) => {
        setIsModalOpen(false);
        const reason = getCloseReasonForAction(closeAction) ?? closeReason;
        try {
            await changeBriefStatus({
                briefId,
                reason: reason?.toString(),
                status:
                    reason === DmBriefCloseReason.DELETED_BRIEF
                        ? Gql.DmBriefStatus.Deleted
                        : Gql.DmBriefStatus.ClosedByBuyer,
            });
            briefStatusChangeClickAction({
                ...reportProps,
                clickedOn: 'yes',
                eventSource: source || '',
                ...(closeAction === CloseAction.SET_TO_INACTIVE &&
                    closeReason !== undefined && { reason: DmBriefCloseReason[closeReason].toString() }),
            });
            if (closeAction !== CloseAction.DELETE_BRIEF) {
                raiseToast({
                    location: 'top',
                    message: appTranslate(`${translationKeyByCloseAction[closeAction]}.toast`),
                    type: 'success',
                });
            }
            onSuccess(closeAction);
        } catch (error) {
            logger.error(error as Error, {
                description: `Failed to change brief status for brief: [${briefId}], closeAction: [${closeAction}], closeReason: [${closeReason}]`,
            });
            onError(closeAction);
            raiseToast({
                location: 'top',
                message: appTranslate('status_change_error_toast'),
                type: 'error',
            });
        }
    };

    const onCloseHandler = () => {
        briefStatusChangeClickAction({
            ...reportProps,
            clickedOn: 'no',
            eventSource: source || '',
        });

        setIsModalOpen(false);
    };

    return {
        isModalOpen,
        setIsModalOpen,
        onConfirmHandler,
        onCloseHandler,
    };
};
